import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { InvoiceLineItemDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface InvoiceLineItemFormProps {
  initialValues: InvoiceLineItemDTO;
  onSubmit: (data: InvoiceLineItemDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const InvoiceLineItemForm: React.FC<InvoiceLineItemFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<InvoiceLineItemDTO>(initialValues);

  const validationRules: ValidationRules = {
    description: [{ required: true, message: 'Description is required' }],
    priceExcl: [
      { required: true, message: 'Price Excl is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Price Excl must be a positive number',
      },
    ],
    priceTax: [
      { required: true, message: 'Price Tax is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Price Tax must be a positive number',
      },
    ],
    priceIncl: [
      { required: true, message: 'Price Incl is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Price Incl must be a positive number',
      },
    ],
    quantity: [
      { required: true, message: 'Quantity is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Quantity must be a positive number',
      },
    ],
    lineExcl: [
      { required: true, message: 'Line Excl is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Line Excl must be a positive number',
      },
    ],
    lineTax: [
      { required: true, message: 'Line Tax is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Line Tax must be a positive number',
      },
    ],
    lineDiscount: [
      { required: true, message: 'Line Discount is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Line Discount must be a positive number',
      },
    ],
    lineIncl: [
      { required: true, message: 'Line Incl is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Line Incl must be a positive number',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<InvoiceLineItemDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="description"
          label="Description"
          type="text"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required={true}
          error={errors.description}
        />
        <FormField
          id="priceExcl"
          label="Price Excl"
          type="number"
          value={formData.priceExcl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Price Excl"
          required={true}
          error={errors.priceExcl}
        />
        <FormField
          id="priceTax"
          label="Price Tax"
          type="number"
          value={formData.priceTax || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Price Tax"
          required={true}
          error={errors.priceTax}
        />
        <FormField
          id="priceIncl"
          label="Price Incl"
          type="number"
          value={formData.priceIncl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Price Incl"
          required={true}
          error={errors.priceIncl}
        />
        <FormField
          id="quantity"
          label="Quantity"
          type="number"
          value={formData.quantity || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Quantity"
          required={true}
          error={errors.quantity}
        />
        <FormField
          id="lineExcl"
          label="Line Excl"
          type="number"
          value={formData.lineExcl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Line Excl"
          required={true}
          error={errors.lineExcl}
        />
        <FormField
          id="lineTax"
          label="Line Tax"
          type="number"
          value={formData.lineTax || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Line Tax"
          required={true}
          error={errors.lineTax}
        />
        <FormField
          id="lineDiscount"
          label="Line Discount"
          type="number"
          value={formData.lineDiscount || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Line Discount"
          required={true}
          error={errors.lineDiscount}
        />
        <FormField
          id="lineIncl"
          label="Line Incl"
          type="number"
          value={formData.lineIncl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Line Incl"
          required={true}
          error={errors.lineIncl}
        />
      </FormSection>
    </Form>
  );
};

export default InvoiceLineItemForm;
